import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import "./AddClientModal.css";

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
];
const NAME_REGEX = /^[A-Za-z][A-Za-z\s'-]*$/;
const CITY_REGEX = /^[A-Za-z][A-Za-z\s.'-]*$/;
const ZIP_REGEX = /^\d{5}(?:-\d{4})?$/;

const validateClientForm = (values, isEditMode = false) => {
  const errors = {};
  const first = String(values.firstName || "").trim();
  const last = String(values.lastName || "").trim();
  const street = String(values.street || "").trim();
  const city = String(values.city || "").trim();
  const state = String(values.state || "").trim().toUpperCase();
  const zip = String(values.zip || "").trim();
  const phoneDigits = String(values.phone || "").replace(/\D/g, "");

  if (!first) errors.firstName = "First name is required.";
  else if (!NAME_REGEX.test(first))
    errors.firstName =
      "First name can only contain letters, spaces, apostrophes, or hyphens.";

  if (!last) errors.lastName = "Last name is required.";
  else if (!NAME_REGEX.test(last))
    errors.lastName =
      "Last name can only contain letters, spaces, apostrophes, or hyphens.";

  if (!isEditMode || street) {
    if (!street) errors.street = "Street address is required.";
  }

  if (!isEditMode || values.phone) {
    if (!phoneDigits) errors.phone = "Phone number is required.";
    else if (phoneDigits.length !== 10)
      errors.phone = "Phone number must be exactly 10 digits.";
  }

  if (!isEditMode || city) {
    if (!city) errors.city = "City is required.";
    else if (!CITY_REGEX.test(city))
      errors.city = "City can only contain letters, spaces, apostrophes, dots, or hyphens.";
  }

  if (!isEditMode || state) {
    if (!state) errors.state = "State is required.";
    else if (!US_STATES.includes(state))
      errors.state = "Please select a valid US state.";
  }

  if (!isEditMode || zip) {
    if (!zip) errors.zip = "ZIP code is required.";
    else if (!ZIP_REGEX.test(zip))
      errors.zip = "ZIP must be 5 digits or ZIP+4 (e.g., 20190 or 20190-1234).";
  }

  return errors;
};

const formatUsPhone = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length !== 10) return String(raw || "").trim();
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const AddClientModal = ({
  isOpen,
  onClose,
  onCreate,
  onSubmit,
  initialClient,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [phone, setPhone] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const isEditMode = Boolean(initialClient);

  const buildTinyAvatarDataUrl = async (dataUrl) => {
    // Goal: keep request body small enough for WAF/proxies by generating a tiny JPEG avatar.
    const img = new Image();
    img.decoding = "async";
    img.src = dataUrl;
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    const sizes = [72, 64, 56, 48];
    const qualities = [0.72, 0.62, 0.52, 0.42];
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported");

    const cropSide = Math.min(img.width || 0, img.height || 0);
    const sx = Math.max(0, Math.floor((img.width - cropSide) / 2));
    const sy = Math.max(0, Math.floor((img.height - cropSide) / 2));

    for (const s of sizes) {
      canvas.width = s;
      canvas.height = s;
      ctx.clearRect(0, 0, s, s);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, sx, sy, cropSide, cropSide, 0, 0, s, s);
      for (const q of qualities) {
        const out = canvas.toDataURL("image/jpeg", q);
        // Keep it very small to avoid WAF/body-size blocks.
        if (out.length <= 7000) return out;
      }
    }

    // Fallback: return the smallest attempt even if slightly larger.
    canvas.width = 48;
    canvas.height = 48;
    ctx.clearRect(0, 0, 48, 48);
    ctx.drawImage(img, sx, sy, cropSide, cropSide, 0, 0, 48, 48);
    return canvas.toDataURL("image/jpeg", 0.4);
  };

  const resetFields = () => {
    setFirstName("");
    setLastName("");
    setStreet("");
    setPhone("");
    setCity("");
    setState("");
    setZip("");
    setAvatarUrl("");
    setFieldErrors({});
  };

  useEffect(() => {
    if (isOpen && initialClient) {
      const fallbackName = (initialClient.name || "").trim().split(" ");
      const fallbackFirst = fallbackName[0] || "";
      const fallbackLast = fallbackName.slice(1).join(" ") || "";
      setFirstName(initialClient.firstName || fallbackFirst);
      setLastName(initialClient.lastName || fallbackLast);
      setStreet(initialClient.street || "");
      setPhone(initialClient.phone || initialClient.phoneNumber || "");
      setCity(initialClient.city || "");
      setState(initialClient.state || "");
      setZip(initialClient.zip || "");
      setAvatarUrl(
        initialClient.avatarUrl ||
          initialClient.photoUrl ||
          initialClient.imageUrl ||
          initialClient.profileImage ||
          initialClient.profilePic ||
          initialClient.pictureUrl ||
          "",
      );
    } else if (isOpen) {
      resetFields();
    }
  }, [isOpen, initialClient]);

  const isFormValid = isEditMode
    ? firstName.trim() !== "" && lastName.trim() !== ""
    : firstName.trim() !== "" &&
      lastName.trim() !== "" &&
      street.trim() !== "" &&
      phone.trim() !== "" &&
      city.trim() !== "" &&
      state.trim() !== "" &&
      zip.trim() !== "";

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateClientForm(
      {
        firstName,
        lastName,
        street,
        phone,
        city,
        state,
        zip,
      },
      isEditMode,
    );
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setErrorText("Please correct the highlighted fields.");
      return;
    }
    // Build full address string
    const stateCode = String(state || "").trim().toUpperCase();
    const zipValue = String(zip || "").trim();
    const cityValue = String(city || "").trim();
    const streetValue = String(street || "").trim();
    const fullAddress = `${streetValue}, ${cityValue}, ${stateCode} ${zipValue}`.trim();
    const payload = {
      firstName: String(firstName || "").trim(),
      lastName: String(lastName || "").trim(),
      street: streetValue,
      phone: formatUsPhone(phone),
      city: cityValue,
      state: stateCode,
      zip: zipValue,
      address: fullAddress,
      avatarUrl,
    };

    const submitFn = isEditMode ? onSubmit || onCreate : onCreate;
    if (submitFn) {
      try {
        setLoading(true);
        setErrorText("");
        // Wait for the async operation to complete before closing
        await submitFn(payload);
        // Don't reset in edit mode—avoids "flash to empty" before modal closes
        if (!isEditMode) resetFields();
        onClose?.();
      } catch (error) {
        console.error("Failed to submit client:", error);
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          (isEditMode
            ? "Failed to update client. Please try again."
            : "Failed to create client. Please try again.");
        setErrorText(msg);
      }
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="add-client-modal">
        <div className="modal-header">
          <div className="header-left">
            <div className="header-icon-badge-blue">
              <FontAwesomeIcon icon={faUserPlus} />
            </div>
            <div className="header-text">
              <h2>{isEditMode ? "Edit Client" : "Add New Client"}</h2>
              <p>
                {isEditMode
                  ? "Update the client's information"
                  : "Enter the client's information"}
              </p>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        

        <div className="modal-scroll">
          {errorText && <div className="form-error">{errorText}</div>}
          <form className="modal-form" onSubmit={handleSubmit}>
            <div className="modal-grid">
              <div className="form-group span-full">
                <label>Profile Photo (JPG, JPEG, PNG)</label>
                <div className="avatar-upload-row">
                  <div className="avatar-preview-circle">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="Client avatar preview" />
                    ) : (
                      <span className="avatar-preview-initial">
                        {(firstName || lastName || "?").trim().charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="avatar-upload-controls">
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={async (e) => {
                        const file = e.target.files && e.target.files[0];
                        if (!file) return;
                        const okType =
                          file.type === "image/jpeg" || file.type === "image/png";
                        if (!okType) {
                          setErrorText("Please upload a JPG, JPEG, or PNG image.");
                          e.target.value = "";
                          return;
                        }
                        // 2MB soft limit (data URLs get big quickly)
                        if (file.size > 2 * 1024 * 1024) {
                          setErrorText("Image too large. Please use a file under 2MB.");
                          e.target.value = "";
                          return;
                        }
                        try {
                          setErrorText("");
                          const reader = new FileReader();
                          const src = await new Promise((resolve, reject) => {
                            reader.onload = () => resolve(reader.result);
                            reader.onerror = reject;
                            reader.readAsDataURL(file);
                          });
                          if (typeof src !== "string") throw new Error("Invalid image");
                          const tiny = await buildTinyAvatarDataUrl(src);
                          if (typeof tiny === "string") {
                            setAvatarUrl(tiny);
                          }
                        } catch (err) {
                          console.error("Failed to process avatar image", err);
                          setErrorText(
                            "Could not process this image. Please try a different JPG/PNG.",
                          );
                          e.target.value = "";
                        }
                      }}
                    />
                    {avatarUrl && (
                      <button
                        type="button"
                        className="avatar-remove-btn"
                        onClick={() => setAvatarUrl("")}
                      >
                        Remove photo
                      </button>
                    )}
                    <div className="avatar-help">
                      Optional. This photo will show in the circle avatar.
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  placeholder="Enter first name..."
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (fieldErrors.firstName)
                      setFieldErrors((prev) => ({ ...prev, firstName: "" }));
                  }}
                  className={fieldErrors.firstName ? "input-invalid" : ""}
                  required
                />
                {fieldErrors.firstName ? (
                  <div className="field-error">{fieldErrors.firstName}</div>
                ) : null}
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  placeholder="Enter last name..."
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (fieldErrors.lastName)
                      setFieldErrors((prev) => ({ ...prev, lastName: "" }));
                  }}
                  className={fieldErrors.lastName ? "input-invalid" : ""}
                  required
                />
                {fieldErrors.lastName ? (
                  <div className="field-error">{fieldErrors.lastName}</div>
                ) : null}
              </div>

              <div className="form-group span-full">
                <label>Street Address *</label>
                <input
                  type="text"
                  placeholder="123 Main St"
                  value={street}
                  onChange={(e) => {
                    setStreet(e.target.value);
                    if (fieldErrors.street)
                      setFieldErrors((prev) => ({ ...prev, street: "" }));
                  }}
                  className={fieldErrors.street ? "input-invalid" : ""}
                  required
                />
                {fieldErrors.street ? (
                  <div className="field-error">{fieldErrors.street}</div>
                ) : null}
              </div>

          <div className="form-group span-full">
            <label>Phone Number *</label>
            <input
              type="tel"
              placeholder="123-456-7890"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value.slice(0, 14));
                if (fieldErrors.phone)
                  setFieldErrors((prev) => ({ ...prev, phone: "" }));
              }}
              className={fieldErrors.phone ? "input-invalid" : ""}
              required
            />
            {fieldErrors.phone ? (
              <div className="field-error">{fieldErrors.phone}</div>
            ) : null}
          </div>

              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  placeholder="City"
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    if (fieldErrors.city)
                      setFieldErrors((prev) => ({ ...prev, city: "" }));
                  }}
                  className={fieldErrors.city ? "input-invalid" : ""}
                  required
                />
                {fieldErrors.city ? (
                  <div className="field-error">{fieldErrors.city}</div>
                ) : null}
              </div>
              <div className="form-group">
                <label>State *</label>
                <select
                  value={state}
                  onChange={(e) => {
                    setState(e.target.value);
                    if (fieldErrors.state)
                      setFieldErrors((prev) => ({ ...prev, state: "" }));
                  }}
                  className={fieldErrors.state ? "input-invalid" : ""}
                  required
                >
                  <option value="">Select state</option>
                  {US_STATES.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
                {fieldErrors.state ? (
                  <div className="field-error">{fieldErrors.state}</div>
                ) : null}
              </div>
              <div className="form-group">
                <label>Zip *</label>
                <input
                  type="text"
                  placeholder="20190 or 20190-1234"
                  value={zip}
                  onChange={(e) => {
                    setZip(e.target.value.slice(0, 10));
                    if (fieldErrors.zip)
                      setFieldErrors((prev) => ({ ...prev, zip: "" }));
                  }}
                  className={fieldErrors.zip ? "input-invalid" : ""}
                  required
                />
                {fieldErrors.zip ? (
                  <div className="field-error">{fieldErrors.zip}</div>
                ) : null}
              </div>
            </div>
          </form>
        </div>

        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={`create-button ${isFormValid ? "active" : ""}`}
            disabled={!isFormValid || loading}
            onClick={handleSubmit}
          >
            {loading
              ? isEditMode
                ? "Saving..."
                : "Creating..."
              : isEditMode
              ? "Save Changes"
              : "Create Client"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddClientModal;
